import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { ArrowRight, Headphones, KeyRound, Layers, ShieldCheck } from 'lucide-react';
import { config, ROUTES } from '../../app/config';
import { realAuthError } from '../../auth/errors';
import { NetworkBackground } from '../../components/visual/NetworkBackground';
import { Button } from '../../components/ui';

/**
 * Pantalla de acceso al ecosistema.
 *
 * Deliberadamente NO hay campos de usuario y contrasena: el formulario de
 * credenciales vive en Keycloak, con todas sus protecciones (brute force,
 * OTP, recuperacion de contrasena, politicas de password). El Ecosystem
 * nunca ve, transporta ni almacena una contrasena.
 *
 * El fondo animado (ver components/visual/network-field.ts) representa los
 * sistemas del ecosistema intercambiando informacion. Es decorativo y no
 * captura el puntero ni el foco.
 */

const HIGHLIGHTS = [
  {
    icon: <KeyRound className="h-4 w-4" aria-hidden />,
    title: 'Un solo inicio de sesion',
    description: 'Autenticate una vez y entra a todo lo que tengas autorizado.',
  },
  {
    icon: <Layers className="h-4 w-4" aria-hidden />,
    title: 'Tus aplicaciones, en un sitio',
    description: 'El launcher muestra unicamente lo que te corresponde.',
  },
  {
    icon: <Headphones className="h-4 w-4" aria-hidden />,
    title: 'Soporte centralizado',
    description: 'Un mismo canal de tickets para todo el ecosistema.',
  },
];

export function LoginPage(): JSX.Element {
  const auth = useAuth();

  // Un `login_required` del sondeo SSO es la respuesta normal cuando todavia
  // no hay sesion: no se muestra como error.
  const fatalError = realAuthError(auth.error);

  /**
   * Sondeo SSO: si ya hay sesion en el realm se entra sin pedir nada.
   *
   * Se ejecuta UNA SOLA VEZ. El objeto `auth` cambia de identidad en cada
   * render, asi que sin este `ref` el efecto se relanzaria tras cada fallo y
   * quedaria reintentando contra Keycloak indefinidamente.
   */
  const probeStarted = useRef(false);
  const [probing, setProbing] = useState(true);

  useEffect(() => {
    if (probeStarted.current) return;
    probeStarted.current = true;

    if (auth.isAuthenticated) {
      setProbing(false);
      return;
    }

    void auth
      .signinSilent()
      .catch(() => undefined)
      .finally(() => setProbing(false));
  }, [auth]);

  const busy = probing || Boolean(auth.activeNavigator);

  if (auth.isAuthenticated) {
    return <Navigate to={ROUTES.home} replace />;
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <NetworkBackground />

      <main className="relative mx-auto flex min-h-screen w-full max-w-6xl flex-col justify-center px-5 py-10 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_auto] lg:gap-16">
          {/* ------------------------------------------------ discurso */}
          <section className="order-2 max-w-xl lg:order-1">
            <span
              className="inline-flex animate-rise items-center gap-2 rounded-full border border-border bg-surface/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-secondary backdrop-blur"
              style={{ animationDelay: '60ms' }}
            >
              <ShieldCheck className="h-3.5 w-3.5 text-primary-bright" aria-hidden />
              Portal del ecosistema
            </span>

            <h1
              className="mt-6 animate-rise text-3xl font-semibold leading-[1.15] tracking-tight text-text-primary sm:text-4xl lg:text-[2.6rem]"
              style={{ animationDelay: '140ms' }}
            >
              Un solo acceso para todas
              <br className="hidden sm:block" /> las aplicaciones de{' '}
              <span className="bg-gradient-to-r from-[#ff5a5c] to-[#c8151d] bg-clip-text text-transparent">
                ElectroDavisan
              </span>
              .
            </h1>

            <div
              className="mt-7 h-px w-24 animate-rise bg-gradient-to-r from-primary to-transparent"
              style={{ animationDelay: '220ms' }}
            />

            <ul className="mt-7 space-y-4">
              {HIGHLIGHTS.map((item, index) => (
                <li
                  key={item.title}
                  className="flex animate-rise items-start gap-3"
                  style={{ animationDelay: `${280 + index * 90}ms` }}
                >
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border bg-surface/70 text-primary-bright backdrop-blur">
                    {item.icon}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-text-primary">
                      {item.title}
                    </span>
                    <span className="block text-[13px] leading-relaxed text-text-muted">
                      {item.description}
                    </span>
                  </span>
                </li>
              ))}
            </ul>

            <p
              className="mt-8 flex animate-rise items-center gap-2 text-[13px] text-text-muted"
              style={{ animationDelay: '580ms' }}
            >
              <span className="relative flex h-2 w-2" aria-hidden>
                <span className="absolute inline-flex h-2 w-2 animate-ping rounded-full bg-success" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
              </span>
              Sesion protegida para personal autorizado
            </p>
          </section>

          {/* -------------------------------------------------- tarjeta */}
          <section className="order-1 w-full lg:order-2 lg:w-[25rem]">
            <div className="relative">
              {/* Halo que respira detras de la tarjeta. */}
              <div
                aria-hidden
                className="absolute -inset-6 animate-breathe rounded-[2rem] bg-primary/20 blur-3xl"
              />

              <div
                className="relative animate-rise overflow-hidden rounded-[1.25rem] border border-border-strong/70 bg-surface/80 shadow-overlay backdrop-blur-xl"
                style={{ animationDelay: '100ms' }}
              >
                {/* Filo superior iluminado. */}
                <div
                  aria-hidden
                  className="h-px w-full bg-gradient-to-r from-transparent via-primary/70 to-transparent"
                />

                <div className="px-7 pb-7 pt-8 sm:px-8">
                  <div className="flex flex-col items-center text-center">
                    {/*
                      El isotipo de verdad, no unas iniciales.

                      Aqui habia un recuadro rojo con las letras «EE» —un
                      marcador de los primeros dias— que sobrevivio a la
                      puesta del logotipo en el resto del portal. Se notaba
                      justo donde peor: esta tarjeta y la pantalla de Keycloak
                      son las dos caras del MISMO inicio de sesion, y una
                      ensenaba el logotipo y la otra unas iniciales.

                      Sin recuadro de color: la imagen ya trae fondo
                      transparente y su propia forma.
                    */}
                    <img
                      src="/ed-ecosystem-logo.png"
                      alt=""
                      aria-hidden
                      width={56}
                      height={56}
                      className="h-14 w-14 shrink-0 object-contain"
                    />
                    <h2 className="mt-4 text-lg font-semibold text-text-primary">
                      {config.appName}
                    </h2>
                    <p className="mt-1 text-[13px] text-text-muted">{config.appTagline}</p>
                  </div>

                  <Button
                    className="group mt-7 w-full"
                    size="lg"
                    loading={busy}
                    onClick={() => {
                      void auth.signinRedirect();
                    }}
                    rightIcon={
                      <ArrowRight
                        className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                        aria-hidden
                      />
                    }
                  >
                    {busy ? 'Conectando...' : 'Iniciar sesion'}
                  </Button>

                  {fatalError ? (
                    <p
                      role="alert"
                      className="mt-4 rounded-lg border border-danger/30 bg-danger-soft p-3 text-[13px] text-danger"
                    >
                      No se pudo contactar con el proveedor de identidad:{' '}
                      {fatalError.message}
                    </p>
                  ) : null}

                  <div className="mt-6 flex items-start gap-2.5 rounded-xl border border-border bg-surface-secondary/60 p-3">
                    <ShieldCheck
                      className="mt-0.5 h-4 w-4 shrink-0 text-success"
                      aria-hidden
                    />
                    <p className="text-[12.5px] leading-relaxed text-text-muted">
                      Seras redirigido al proveedor de identidad para autenticarte mediante
                      OpenID Connect. Tus credenciales nunca pasan por {config.appName}.
                    </p>
                  </div>
                </div>

                <div className="border-t border-border bg-surface-secondary/40 px-7 py-3 text-center sm:px-8">
                  <p className="text-[11.5px] text-text-muted">
                    Acceso exclusivo para usuarios autorizados
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
